import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import type { Movie } from '@/mocks/content';

interface HeroBannerProps {
  slides: Movie[];
}

export default function HeroBanner({ slides }: HeroBannerProps) {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goTo = useCallback((index: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrent(index);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [isTransitioning]);

  const next = useCallback(() => {
    goTo((current + 1) % slides.length);
  }, [current, slides.length, goTo]);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  if (slides.length === 0) return null;

  const movie = slides[current];

  return (
    <section className="relative w-full h-[520px] md:h-[620px] overflow-hidden" aria-label="热门推荐轮播">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={movie.poster}
          alt=""
          className="w-full h-full object-cover object-top"
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-background-50" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full h-full max-w-[1400px] mx-auto px-4 md:px-6 flex flex-col justify-center pt-16">
        <div className="max-w-xl" key={movie.id}>
          <div className="flex items-center gap-3 mb-4">
            <span className="text-xs font-medium bg-accent-500 text-background-50 px-3 py-1 rounded-full">
              {movie.isHot ? '正在热播' : '重磅推荐'}
            </span>
            <span className="text-white/60 text-sm">{movie.year}</span>
            <span className="text-white/60 text-sm">{movie.subCategory}</span>
          </div>
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-white mb-3">
            {movie.title}
          </h1>
          <p className="text-white/70 text-sm md:text-base leading-relaxed mb-6 line-clamp-3">
            {movie.description}
          </p>
          <div className="flex items-center gap-3">
            <Link
              to={`/detail/${movie.id}`}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-lg hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-play-fill" />
              立即观看
            </Link>
            <span className="text-white/50 text-sm flex items-center gap-1">
              <i className="ri-star-fill text-accent-400" />
              {movie.rating}
            </span>
          </div>
        </div>

        {/* Dots */}
        <div className="flex items-center gap-2 mt-10">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goTo(index)}
              className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                index === current ? 'w-8 bg-primary-500' : 'w-4 bg-white/40 hover:bg-white/60'
              }`}
              aria-label={`切换到第 ${index + 1} 张`}
            />
          ))}
        </div>
      </div>

      {/* Arrows */}
      <button
        onClick={() => goTo((current - 1 + slides.length) % slides.length)}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white backdrop-blur-sm transition-all duration-200 cursor-pointer"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-xl" />
      </button>
      <button
        onClick={() => goTo((current + 1) % slides.length)}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white backdrop-blur-sm transition-all duration-200 cursor-pointer"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-xl" />
      </button>
    </section>
  );
}